import React from 'react';
import '../styles/glass-table.css';

/* GlassDataTable — Liquid-Glass data table.
   Accepts a { columns:[{label, field, sort}], rows:[{...}] } shape.
   Renders pure glass markup (no Bootstrap). Client-side search + sort +
   paging. */

const cellText = (v) => {
  if (v == null) return '';
  if (typeof v === 'object') return ''; // React element — not searchable/sortable
  return String(v);
};

class GlassDataTable extends React.Component {
  state = { query: '', page: 1, sortField: null, sortDir: 'asc' };

  render() {
    const {
      data = {}, searching = true, paging = true, entries = 10,
      className, onSearch, searchPlaceholder = 'Search…', emptyText = 'No matching records found',
    } = this.props;
    const columns = data.columns || [];
    const rows = data.rows || [];
    const { query, sortField, sortDir } = this.state;

    let filtered = rows;
    if (query) {
      const q = query.toLowerCase();
      filtered = rows.filter((r) => columns.some((c) => c.searchable !== false && cellText(r[c.field]).toLowerCase().includes(q)));
    }
    if (sortField) {
      filtered = filtered.slice().sort((a, b) => {
        const cmp = cellText(a[sortField]).localeCompare(cellText(b[sortField]), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    const total = filtered.length;
    const perPage = paging ? (entries || 10) : (total || 1);
    const pageCount = Math.max(1, Math.ceil(total / perPage));
    const cur = Math.min(this.state.page, pageCount);
    const start = (cur - 1) * perPage;
    const pageRows = paging ? filtered.slice(start, start + perPage) : filtered;

    return (
      <div className={['gdt', className].filter(Boolean).join(' ')}>
        {searching && (
          <div className="gdt-search">
            <i className="fas fa-magnifying-glass"></i>
            <input
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => { this.setState({ query: e.target.value, page: 1 }); if (onSearch) onSearch(e.target.value); }}
            />
          </div>
        )}
        <div className="gdt-scroll">
          <table className="gdt-table">
            <thead>
              <tr>
                {columns.map((c, i) => {
                  const sortable = c.sort !== 'disabled';
                  const isActive = sortField === c.field;
                  return (
                    <th
                      key={i}
                      className={sortable ? 'sortable' : undefined}
                      onClick={() => {
                        if (!sortable) return;
                        this.setState((s) => ({
                          sortField: c.field,
                          sortDir: s.sortField === c.field && s.sortDir === 'asc' ? 'desc' : 'asc',
                        }));
                      }}
                    >
                      <span>{c.label}</span>
                      {sortable && (
                        <i className={`fas fa-sort${isActive ? (sortDir === 'asc' ? '-up' : '-down') : ''} gdt-sort ${isActive ? 'on' : ''}`}></i>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr><td className="gdt-empty" colSpan={columns.length || 1}>{emptyText}</td></tr>
              )}
              {pageRows.map((r, ri) => (
                <tr key={ri}>
                  {columns.map((c, ci) => <td key={ci}>{r[c.field]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {paging && total > perPage && (
          <div className="gdt-foot">
            <span className="gdt-count">
              {total === 0 ? 'No entries' : `${start + 1}–${Math.min(start + perPage, total)} of ${total}`}
            </span>
            <div className="gdt-pager">
              <button type="button" disabled={cur <= 1} onClick={() => this.setState({ page: cur - 1 })}>
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="gdt-page">{cur} / {pageCount}</span>
              <button type="button" disabled={cur >= pageCount} onClick={() => this.setState({ page: cur + 1 })}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default GlassDataTable;
